import { ColorEnum } from '@/constants/color'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface HistoryEventView {
    id: string
    time: string
    elapsed?: string
    title: string
    description?: string[]
    amount?: string
    tone?: 'positive' | 'neutral'
}

interface HistoryProps {
    events: HistoryEventView[]
}

const History: FC<HistoryProps> = ({ events }) => {
    const { styles } = useStyles(stylesheet)

    return (
        <View style={styles.container}>
            {events.map((event) => (
                <View key={event.id} style={styles.row}>
                    <View style={styles.time}>
                        <Typography.Description font="bold" size="large">
                            {event.time}
                        </Typography.Description>
                        {event.elapsed && (
                            <Typography.Description size="small" style={styles.secondary}>
                                {event.elapsed}
                            </Typography.Description>
                        )}
                    </View>
                    <View style={styles.description}>
                        <Typography.Description font="medium" size="large">
                            {event.title}
                        </Typography.Description>
                        {event.description?.map((description, index) => (
                            <Typography.Description
                                key={`${description}-${index}`}
                                size="small"
                                style={styles.secondary}
                            >
                                {description}
                            </Typography.Description>
                        ))}
                    </View>
                    {event.amount && (
                        <Typography.Description
                            font="bold"
                            size="large"
                            style={styles.amount(event.tone ?? 'neutral')}
                        >
                            {event.amount}
                        </Typography.Description>
                    )}
                </View>
            ))}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        width: '100%',
        borderTopWidth: 1,
        borderTopColor: ColorEnum.GRAY_200,
    },
    row: {
        minHeight: 68,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_200,
    },
    time: {
        width: 60,
        gap: 4,
    },
    description: {
        flex: 1,
        minWidth: 0,
        gap: 2,
    },
    secondary: {
        color: ColorEnum.GRAY_600,
    },
    amount: (tone: NonNullable<HistoryEventView['tone']>) => ({
        color: tone === 'positive' ? ColorEnum.EMERALD_700 : ColorEnum.GRAY_900,
        textAlign: 'right',
    }),
})

export default History
